import { ɵLocaleDataIndex } from '@angular/core';
import { FeatureFlag, featureIsEnabled } from '../services/feature-flag';

export type CurrenciesSymbols = [string] | [string | undefined, string];

export interface LocaleCurrenciesData {
  [code: string]: CurrenciesSymbols;
}

export interface LocaleData {
  [ɵLocaleDataIndex.Currencies]: LocaleCurrenciesData;
  [ɵLocaleDataIndex.Directionality]: 'ltr' | 'rtl';
}

export const currencyData: Array<{
  code: string;
  name: string;
  // SEE: https://docs.microsoft.com/en-us/openspecs/office_standards/ms-oe376/6c085406-a698-4e12-9d4d-c3b0ee3dbc4a
  // These could also use LOCALE_ID to differentiate between Euros in France, Germany, etc or Swiss Francs in French, German or Italian
  lcid: number;
  symbol: string;
}> = [
  { code: 'AED', name: 'Emirati Dirham', lcid: 14337, symbol: 'د.إ.\u200F' },
  { code: 'AFN', name: 'Afghan Afghani', lcid: 1164, symbol: '؋' },
  { code: 'ALL', name: 'Albanian Lek', lcid: 1052, symbol: 'L' },
  { code: 'AMD', name: 'Armenian Dram', lcid: 1067, symbol: '֏' },
  { code: 'ANG', name: 'Netherlands Antillean Guilder', lcid: 4096, symbol: 'ƒ' },
  { code: 'AOA', name: 'Angolan Kwanza', lcid: 4096, symbol: 'Kz' },
  { code: 'ARS', name: 'Argentine Peso', lcid: 11274, symbol: '$' },
  { code: 'AUD', name: 'Australian Dollar', lcid: 3081, symbol: '$' },
  { code: 'AWG', name: 'Aruban Florin', lcid: 4096, symbol: 'ƒ' },
  { code: 'AZN', name: 'Azerbaijani Manat', lcid: 1068, symbol: '₼' },
  { code: 'BAM', name: 'Bosnia and Herzegovina Convertible Mark', lcid: 5146, symbol: 'КМ' },
  { code: 'BBD', name: 'Barbadian Dollar', lcid: 4096, symbol: '$' },
  { code: 'BDT', name: 'Bangladeshi Taka', lcid: 2117, symbol: '৳' },
  { code: 'BGN', name: 'Bulgarian Lev', lcid: 1026, symbol: 'лв.' },
  { code: 'BHD', name: 'Bahraini Dinar', lcid: 15361, symbol: 'د.ب.\u200F' },
  { code: 'BIF', name: 'Burundian Franc', lcid: 4096, symbol: 'FBu' },
  { code: 'BMD', name: 'Bermudian Dollar', lcid: 4096, symbol: '$' },
  { code: 'BND', name: 'Brunei Dollar', lcid: 2110, symbol: '$' },
  { code: 'BOB', name: 'Bolivian Boliviano', lcid: 16394, symbol: 'Bs' },
  { code: 'BRL', name: 'Brazilian Real', lcid: 1046, symbol: 'R$' },
  { code: 'BSD', name: 'Bahamian Dollar', lcid: 4096, symbol: '$' },
  { code: 'BTN', name: 'Bhutanese Ngultrum', lcid: 3153, symbol: 'Nu.' },
  { code: 'BWP', name: 'Botswana Pula', lcid: 2098, symbol: 'P' },
  { code: 'BYN', name: 'Belarusian Ruble', lcid: 1059, symbol: 'Br' },
  { code: 'BZD', name: 'Belize Dollar', lcid: 10249, symbol: '$' },
  { code: 'CAD', name: 'Canadian Dollar', lcid: 4105, symbol: '$' },
  { code: 'CDF', name: 'Congolese Franc', lcid: 9228, symbol: 'FC' },
  { code: 'CHF', name: 'Swiss Franc', lcid: 2055, symbol: 'Fr.' },
  { code: 'CLP', name: 'Chilean Peso', lcid: 13322, symbol: '$' },
  { code: 'CNY', name: 'Chinese Yuan Renminbi', lcid: 2052, symbol: '¥' },
  { code: 'COP', name: 'Colombian Peso', lcid: 9226, symbol: '$' },
  { code: 'CRC', name: 'Costa Rican Colon', lcid: 5130, symbol: '₡' },
  { code: 'CUP', name: 'Cuban Peso', lcid: 23562, symbol: '$' },
  { code: 'CVE', name: 'Cape Verdean Escudo', lcid: 4096, symbol: '$' },
  { code: 'CZK', name: 'Czech Koruna', lcid: 1029, symbol: 'Kč' },
  { code: 'DJF', name: 'Djiboutian franc', lcid: 4096, symbol: 'Fdj' },
  { code: 'DKK', name: 'Danish Krone', lcid: 1030, symbol: 'kr.' },
  { code: 'DOP', name: 'Dominican Peso', lcid: 7178, symbol: '$' },
  { code: 'DZD', name: 'Algerian Dinar', lcid: 5121, symbol: 'د.ج.\u200F' },
  { code: 'EGP', name: 'Egyptian Pound', lcid: 3073, symbol: 'ج.م.\u200F' },
  { code: 'ERN', name: 'Eritrean nakfa', lcid: 4096, symbol: 'Nfk' },
  { code: 'ETB', name: 'Ethiopian Birr', lcid: 1138, symbol: 'Br' },
  { code: 'EUR', name: 'Euro', lcid: 6153, symbol: '€' },
  { code: 'FJD', name: 'Fijian Dollar', lcid: 4096, symbol: '$' },
  { code: 'GBP', name: 'British Pound', lcid: 2057, symbol: '£' },
  { code: 'GEL', name: 'Georgian lari', lcid: 1079, symbol: '₾' },
  { code: 'GHS', name: 'Ghanaian Cedi', lcid: 4096, symbol: 'GH₵' },
  { code: 'GIP', name: 'Gibraltar Pound', lcid: 4096, symbol: '£' },
  { code: 'GMD', name: 'Gambian Dalasi', lcid: 4096, symbol: 'D' },
  { code: 'GNF', name: 'Guinean Franc', lcid: 4096, symbol: 'FG' },
  { code: 'GTQ', name: 'Guatemalan Quetzal', lcid: 4106, symbol: 'Q' },
  { code: 'GYD', name: 'Guyanese Dollar', lcid: 4096, symbol: '$' },
  { code: 'HKD', name: 'Hong Kong Dollar', lcid: 3076, symbol: '$' },
  { code: 'HNL', name: 'Honduran Lempira', lcid: 18442, symbol: 'L' },
  { code: 'HRK', name: 'Croatian Kuna', lcid: 1050, symbol: 'kn' },
  { code: 'HTG', name: 'Haitian Gourde', lcid: 15372, symbol: 'G' },
  { code: 'HUF', name: 'Hungarian Forint', lcid: 1038, symbol: 'Ft' },
  { code: 'IDR', name: 'Indonesian Rupiah', lcid: 1057, symbol: 'Rp' },
  { code: 'ILS', name: 'Israeli New Shekel', lcid: 1037, symbol: '₪' },
  { code: 'INR', name: 'Indian Rupee', lcid: 1081, symbol: '₹' },
  { code: 'IQD', name: 'Iraqi Dinar', lcid: 2049, symbol: 'د.ع.\u200F' },
  { code: 'IRR', name: 'Iranian Rial', lcid: 1065, symbol: 'ریال' },
  { code: 'ISK', name: 'Icelandic Krona', lcid: 1039, symbol: 'kr' },
  { code: 'JMD', name: 'Jamaican Dollar', lcid: 8201, symbol: '$' },
  { code: 'JOD', name: 'Jordanian Dinar', lcid: 11265, symbol: 'د.أ.\u200F' },
  { code: 'JPY', name: 'Japanese Yen', lcid: 1041, symbol: '¥' },
  { code: 'KES', name: 'Kenyan Shilling', lcid: 1089, symbol: 'KSh' },
  { code: 'KGS', name: 'Kyrgyzstani Som', lcid: 1088, symbol: 'сом' },
  { code: 'KHR', name: 'Cambodian Riel', lcid: 1107, symbol: '៛' },
  { code: 'KMF', name: 'Comorian Franc', lcid: 4096, symbol: 'CF' },
  { code: 'KRW', name: 'South Korean Won', lcid: 1042, symbol: '₩' },
  { code: 'KWD', name: 'Kuwaiti Dinar', lcid: 13313, symbol: 'د.ك.\u200F' },
  { code: 'KYD', name: 'Cayman Islands Dollar', lcid: 4096, symbol: '$' },
  { code: 'KZT', name: 'Kazakhstani Tenge', lcid: 1087, symbol: '₸' },
  { code: 'LAK', name: 'Lao Kip', lcid: 1108, symbol: '₭' },
  { code: 'LBP', name: 'Lebanese Pound', lcid: 12289, symbol: 'ل.ل.\u200F' },
  { code: 'LKR', name: 'Sri Lankan Rupee', lcid: 1115, symbol: 'රු.' },
  { code: 'LRD', name: 'Liberian Dollar', lcid: 4096, symbol: '$' },
  { code: 'LYD', name: 'Libyan Dinar', lcid: 4097, symbol: 'د.ل.\u200F' },
  { code: 'MAD', name: 'Moroccan Dirham', lcid: 6145, symbol: 'د.م.\u200F' },
  { code: 'MDL', name: 'Moldovan Leu', lcid: 2072, symbol: 'L' },
  { code: 'MGA', name: 'Malagasy Ariary', lcid: 4096, symbol: 'Ar' },
  { code: 'MKD', name: 'Macedonian Denar', lcid: 1071, symbol: 'ден' },
  { code: 'MMK', name: 'Myanmar Kyat', lcid: 1109, symbol: 'K' },
  { code: 'MNT', name: 'Mongolian Tögrög', lcid: 2128, symbol: '₮' },
  { code: 'MOP', name: 'Macanese Pataca', lcid: 5124, symbol: '$' },
  { code: 'MRU', name: 'Mauritanian Ouguiya', lcid: 4096, symbol: 'أ.م.\u200F' },
  { code: 'MUR', name: 'Mauritian Rupee', lcid: 4096, symbol: 'Rs' },
  { code: 'MVR', name: 'Maldivian Rufiyaa', lcid: 1125, symbol: 'ރ.\u200F' },
  { code: 'MWK', name: 'Malawian Kwacha', lcid: 4096, symbol: 'K' },
  { code: 'MXN', name: 'Mexican Peso', lcid: 2058, symbol: '$' },
  { code: 'MYR', name: 'Malaysian Ringgit', lcid: 1086, symbol: 'RM' },
  { code: 'MZN', name: 'Mozambican Metical', lcid: 4096, symbol: 'MT' },
  { code: 'NAD', name: 'Namibian Dollar', lcid: 4096, symbol: '$' },
  { code: 'NGN', name: 'Nigerian Naira', lcid: 1136, symbol: '₦' },
  { code: 'NIO', name: 'Nicaraguan Córdoba', lcid: 19466, symbol: 'C$' },
  { code: 'NOK', name: 'Norwegian Krone', lcid: 2068, symbol: 'kr' },
  { code: 'NPR', name: 'Nepalese Rupee', lcid: 1121, symbol: 'रू' },
  { code: 'NZD', name: 'New Zealand Dollar', lcid: 5129, symbol: '$' },
  { code: 'OMR', name: 'Omani Rial', lcid: 8193, symbol: 'ر.ع.\u200F' },
  { code: 'PAB', name: 'Panamanian Balboa', lcid: 6154, symbol: 'B/.' },
  { code: 'PEN', name: 'Peruvian Sol', lcid: 10250, symbol: 'S/' },
  { code: 'PGK', name: 'Papua New Guinean Kina', lcid: 4096, symbol: 'K' },
  { code: 'PHP', name: 'Philippine Peso', lcid: 1124, symbol: '₱' },
  { code: 'PKR', name: 'Pakistani Rupee', lcid: 1056, symbol: 'Rs' },
  { code: 'PLN', name: 'Polish Zloty', lcid: 1045, symbol: 'zł' },
  { code: 'PYG', name: 'Paraguayan Guarani', lcid: 153700, symbol: '₲' },
  { code: 'QAR', name: 'Qatari Riyal', lcid: 16385, symbol: 'ر.ق.\u200F' },
  { code: 'RON', name: 'Romanian Leu', lcid: 1048, symbol: 'L' },
  { code: 'RSD', name: 'Serbian Dinar', lcid: 2074, symbol: 'дин' },
  { code: 'RUB', name: 'Russian Ruble', lcid: 1049, symbol: '₽' },
  { code: 'RWF', name: 'Rwandan Franc', lcid: 4096, symbol: 'RF' },
  { code: 'SAR', name: 'Saudi Riyal', lcid: 1025, symbol: 'ر.س.\u200F' },
  { code: 'SBD', name: 'Solomon Islands Dollar', lcid: 4096, symbol: '$' },
  { code: 'SCR', name: 'Seychellois Rupee', lcid: 4096, symbol: 'Rs' },
  { code: 'SDG', name: 'Sudanese Pound', lcid: 4096, symbol: 'ج.س.\u200F' },
  { code: 'SEK', name: 'Swedish Krona', lcid: 1053, symbol: 'kr' },
  { code: 'SGD', name: 'Singapore Dollar', lcid: 18441, symbol: '$' },
  { code: 'SHP', name: 'Saint Helena Pound', lcid: 4096, symbol: '£' },
  { code: 'SLL', name: 'Sierra Leonean Leone', lcid: 4096, symbol: 'Le' },
  { code: 'SOS', name: 'Somali Shilling', lcid: 1143, symbol: 'S' },
  { code: 'SRD', name: 'Surinamese Dollar', lcid: 4096, symbol: '$' },
  { code: 'SSP', name: 'South Sudanese Pound', lcid: 4096, symbol: '£' },
  { code: 'STN', name: 'Sao Tome and Principe Dobra', lcid: 4096, symbol: 'Db' },
  { code: 'SYP', name: 'Syrian Pound', lcid: 10241, symbol: 'ل.س.\u200F' },
  { code: 'SZL', name: 'Swazi Lilangeni', lcid: 4096, symbol: 'E' },
  { code: 'THB', name: 'Thai Baht', lcid: 1054, symbol: '฿' },
  { code: 'TJS', name: 'Tajikistani Somoni', lcid: 1064, symbol: 'SM' },
  { code: 'TMT', name: 'Turkmenistan Manat', lcid: 1090, symbol: 'm' },
  { code: 'TND', name: 'Tunisian Dinar', lcid: 7169, symbol: 'د.ت.\u200F' },
  { code: 'TOP', name: 'Tongan Paʻanga', lcid: 4096, symbol: 'T$' },
  { code: 'TRY', name: 'Turkish Lira', lcid: 1055, symbol: '₺' },
  { code: 'TTD', name: 'Trinidad and Tobago Dollar', lcid: 11273, symbol: '$' },
  { code: 'TWD', name: 'New Taiwan Dollar', lcid: 1028, symbol: '$' },
  { code: 'TZS', name: 'Tanzanian Shilling', lcid: 4096, symbol: 'TSh' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', lcid: 1058, symbol: '₴' },
  { code: 'UGX', name: 'Ugandan shilling', lcid: 4096, symbol: 'USh' },
  { code: 'USD', name: 'US Dollar', lcid: 1033, symbol: '$' },
  { code: 'UYU', name: 'Uruguayan Peso', lcid: 14346, symbol: '$' },
  { code: 'UZS', name: 'Uzbekistani Sum', lcid: 1091, symbol: 'сўм' },
  { code: 'VES', name: 'Venezuelan Bolívar', lcid: 8202, symbol: 'Bs.S' },
  { code: 'VND', name: 'Vietnamese Dong', lcid: 1066, symbol: '₫' },
  { code: 'VUV', name: 'Vanuatu vatu', lcid: 4096, symbol: 'VT' },
  { code: 'WST', name: 'Samoan Tala', lcid: 4096, symbol: '$' },
  { code: 'XAF', name: 'Central African CFA Franc', lcid: 4096, symbol: 'Fr' },
  { code: 'XCD', name: 'East Caribbean Dollar', lcid: 9225, symbol: '$' },
  { code: 'XOF', name: 'West African CFA Franc', lcid: 4096, symbol: 'Fr' },
  { code: 'YER', name: 'Yemeni Rial', lcid: 9217, symbol: 'ر.ي.\u200F' },
  { code: 'ZAR', name: 'South African Rand', lcid: 7177, symbol: 'R' },
  { code: 'ZMW', name: 'Zambian kwacha', lcid: 4096, symbol: 'K' },
  { code: 'ZWL', name: 'Zimbabwean dollar', lcid: 12297, symbol: '$' },
  { code: 'XXX', name: '(None)', lcid: 127, symbol: '¤' },
];

export const CURRENCIES: ReadonlyArray<{ code: string; name: string }> = (
  featureIsEnabled(FeatureFlag.NoCurrency)
    ? currencyData
    : currencyData.filter(c => c.code !== 'XXX')
).map(c => ({
  code: c.code,
  name: c.name,
}));
export const CURRENCY_LCID: Readonly<Record<string, number>> = Object.fromEntries(
  currencyData.map(c => [c.code, c.lcid] as const),
);

const decorateWithCurrency = (
  localeData: LocaleData,
  currencyCode: string,
  symbols: CurrenciesSymbols,
): LocaleData => {
  const localeCurrencies = localeData[ɵLocaleDataIndex.Currencies];

  return {
    ...localeData,
    [ɵLocaleDataIndex.Currencies]: {
      ...localeCurrencies,
      [currencyCode]: symbols,
    },
  };
};

export const addMissingCurrencies = (localeData: LocaleData): LocaleData => {
  let result = localeData;

  currencyData.forEach(c => {
    result = decorateWithCurrency(result, c.code, [undefined, c.symbol]);
  });

  return result;
};
/* List of Angular supported currencies (for en-AU)
'AUD': ['$'],
'BDT': [u, 'Tk'],
'BOB': [u, '$b'],
'BRL': [u, 'R$'],
'CAD': [u, '$'],
'CNY': [u, '¥'],
'CUP': [u, '₱'],
'EGP': [u, '£'],
'EUR': [u, '€'],
'GBP': [u, '£'],
'HKD': [u, '$'],
'ILS': [u, '₪'],
'INR': [u, '₹'],
'ISK': [u, 'Kr'],
'JPY': [u, '¥'],
'KRW': [u, '₩'],
'MXN': [u, '$'],
'NZD': [u, '$'],
'PYG': [u, 'Gs'],
'SCR': ['Rs'],
'SEK': [u, 'Kr'],
'TWD': [u, '$'],
'USD': [u, '$'],
'UYU': [u, '$U'],
'VND': [u, '₫'],
'XAF': [],
'XCD': [u, '$'],
'XOF': [],
'XPF': ['CFP']
*/
